import { normalizePaywallSource } from "@/lib/analytics/paywall-source";
import { getBrowserReturnTo, sanitizeInternalReturnTo } from "@/lib/paywall/recovery";
import { schoolHref } from "@/lib/school-routes";

const COUPON_MAX_CHARS = 128;

export function getBrowserCouponCode(): string | null {
  if (typeof window !== "undefined") {
    const couponFromUrl = new URLSearchParams(window.location.search).get("coupon")?.trim();
    if (couponFromUrl) {
      return couponFromUrl.slice(0, COUPON_MAX_CHARS);
    }
  }

  if (typeof document === "undefined") return null;

  const match = document.cookie.match(/(?:^|;\s*)coupon_code=([^;]+)/);
  if (!match?.[1]) return null;

  try {
    return decodeURIComponent(match[1]).trim().slice(0, COUPON_MAX_CHARS) || null;
  } catch {
    return match[1].trim().slice(0, COUPON_MAX_CHARS) || null;
  }
}

/**
 * Internal paywall link. Campaign attribution stays in browser storage: copying
 * it into this URL would make analytics read an internal click as a new
 * campaign touch.
 */
export function buildTrackedPaywallUrl({
  coupon,
  extraParams,
  mode,
  returnTo = getBrowserReturnTo(),
  source,
}: {
  coupon?: string | null;
  extraParams?: Record<string, string | null | undefined>;
  mode?: string | null;
  returnTo?: string | null;
  source?: string | null;
} = {}): string {
  const params = new URLSearchParams();
  const resolvedCoupon = coupon || getBrowserCouponCode();

  if (resolvedCoupon) params.set("coupon", resolvedCoupon);
  params.set("source", normalizePaywallSource(source));
  if (mode) params.set("mode", mode);
  params.set("return_to", sanitizeInternalReturnTo(returnTo));

  if (extraParams) {
    Object.entries(extraParams).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
  }

  const query = params.toString();
  return schoolHref(query ? `/paywall?${query}` : "/paywall", returnTo);
}
